import React, { useCallback, useEffect, useState } from "react";
import { Spinner } from "react-bootstrap";
import dayjs from "dayjs";
import { useParams } from "react-router-dom";
import { getWaitlistLeaveApi, postWaitlistLeaveApi } from "../../api/api";
import { ResultShell } from "../cumpleanos/BirthdaySteps";
import "../reservas/reservas.css";
import "../cumpleanos/cumpleanos.css";

const Shell = ({ children }) => <div className="reservas-root bday">{children}</div>;

const Row = ({ label, value }) =>
  value ? (
    <div className="bday-row">
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  ) : null;

/** Public page of the leave link (/lista-de-espera/baja/:token). Works without login. */
const WaitlistLeave = () => {
  const { token } = useParams();
  // status: loading | ready | invalid | gone | error | cancelled | done
  const [state, setState] = useState({ status: "loading", entry: null });
  const [leaving, setLeaving] = useState(false);
  const [formError, setFormError] = useState("");
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let active = true;
    getWaitlistLeaveApi(token)
      .then((res) => {
        if (!active) return;
        // The back wraps the body in its usual { isSuccess, status, info } envelope.
        const entry = res?.data?.info?.entry;
        if (!entry) return setState({ status: "invalid", entry: null });
        if (entry.status === "cancelled") return setState({ status: "cancelled", entry });
        if (entry.status === "booked" || entry.status === "expired") return setState({ status: "gone", entry });
        setState({ status: "ready", entry });
      })
      .catch((err) => {
        if (!active) return;
        // A bad or unknown token looks the same on purpose.
        setState({ status: err?.response?.status === 404 ? "invalid" : "error", entry: null });
      });
    return () => {
      active = false;
    };
  }, [token, reloadKey]);

  const reload = useCallback(() => {
    setState({ status: "loading", entry: null });
    setReloadKey((k) => k + 1);
  }, []);

  const leave = async () => {
    if (leaving) return;
    setLeaving(true);
    setFormError("");
    try {
      await postWaitlistLeaveApi(token);
      setState((s) => ({ ...s, status: "done" }));
    } catch (err) {
      const code = err?.response?.status;
      if (code === 409) setState((s) => ({ ...s, status: "gone" }));
      else if (code === 404) setState({ status: "invalid", entry: null });
      else setFormError("No pudimos darte de baja. Probá de nuevo.");
    } finally {
      setLeaving(false);
    }
  };

  if (state.status === "loading") {
    return (
      <Shell>
        <div className="bday-state">
          <Spinner animation="border" />
        </div>
      </Shell>
    );
  }
  if (state.status === "invalid") {
    return (
      <Shell>
        <ResultShell icon="search" tone="neutral" title="Link inválido o vencido">
          <p>Revisá que lo hayas copiado completo desde el mensaje.</p>
        </ResultShell>
      </Shell>
    );
  }
  if (state.status === "error") {
    return (
      <Shell>
        <ResultShell icon="alert" tone="danger" title="No pudimos cargar tu anotación">
          <p>Probá de nuevo en unos segundos.</p>
          <div className="bday-state-actions">
            <button type="button" className="r-btn-primary" onClick={reload}>Reintentar</button>
          </div>
        </ResultShell>
      </Shell>
    );
  }
  if (state.status === "gone") {
    return (
      <Shell>
        <ResultShell icon="check" tone="neutral" title="Esta anotación ya no está activa">
          <p>Ya reservaste ese lugar o la anotación venció. No hace falta que hagas nada.</p>
        </ResultShell>
      </Shell>
    );
  }
  if (state.status === "cancelled") {
    return (
      <Shell>
        <ResultShell icon="check" title="Ya te diste de baja" />
      </Shell>
    );
  }
  if (state.status === "done") {
    return (
      <Shell>
        <ResultShell icon="check" title="Listo, te diste de baja">
          <p>No te vamos a avisar más por esta anotación.</p>
        </ResultShell>
      </Shell>
    );
  }

  const { entry } = state;
  return (
    <Shell>
      <ResultShell icon="cal" tone="neutral" title="¿Querés dejar la lista de espera?">
        <div className="bday-summary">
          <Row label="Lugar" value={entry.venue?.name} />
          <Row label="Día" value={entry.date ? dayjs(entry.date).format("DD/MM/YYYY") : ""} />
        </div>
        {formError && <div className="rv-form-error" role="alert">{formError}</div>}
        <div className="bday-state-actions">
          <button type="button" className="r-btn-primary" onClick={leave} disabled={leaving}>
            {leaving ? <Spinner animation="border" size="sm" /> : "Darme de baja"}
          </button>
        </div>
      </ResultShell>
    </Shell>
  );
};

export default WaitlistLeave;
